import { memo, useEffect, useRef, useState } from "react";
import { sendInfoWanrSeenCart } from "~/features/cart/cart/api/sendInfoWanrSeenCart";
import { useSelector } from "react-redux";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import Spinner from "~/common/components/Spinner/Spinner";
import ProductTableV2 from "~/features/cart/cartV2/ProductTable";
import { OrderSummary } from "~/features/cart/cartV2/OrderSummary";
import FullscreenLoader from "~/features/loading/FullscreenLoader";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";
import { PunchoutCancelRequisition, PunchoutPlaceRequisition } from "~/features/cart/cart/Punchout/PunchoutButtons";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { CART_PNA_THRESHOLD } from "~/utils/constants";
import { CheckoutButton } from "~/features/cart/cart/components/Buttons/CheckoutButton";
import CheckMessagesComponent from "~/common/components/CheckMessagesComponent";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { useTranslation } from "react-i18next";
import { useCartPageContext } from "~/features/cart/cart/CartPageContext";
import { IsBundledQuoteContextProvider } from "~/features/cart/cart/IsBundledQuoteContext";
import { Box, Stack } from "~/common/mui";
import { selectIsDstError } from "~/features/cart/cart/redux/selectors/cartPageSelectors";
import { isPunchoutEnabled } from "~/features/cart/cart/Punchout/punchoutConstants";
import { Alert } from "~/common/mui/Alert/Alert";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { CartQuickActionsButton } from "~/features/cart/cart/components/Buttons/CartQuickActionsButton";
import ConnectedCPDProgress from "~/features/cms/homepagePersonalization/cpd/components/cpdProgress/ConnectedCPDProgress";
import type { ProductEntryListRef } from "~/features/cart/cartV2/ProductEntryList";
import { CartSectionTitle } from "../../checkout/components/CartSectionTitle";
import type { ProductData } from "~/domains/product/types/ProductData";
import { useB2BCartPageViewModel } from "~/features/cart/cart/hooks/useB2BCartPageViewModel";
import { parseAsBoolean, useQueryState } from "nuqs";

/**
 * This variant of the cart page is used by:
 *
 * - Punchout
 * - B2B aka "Customers"
 */
export const B2BCartPage = memo(function B2BCartPage() {
  const { t } = useTranslation();
  const [recentlyAddedItem, setRecentlyAddedItem] = useState<ProductData | null>(null);

  const [quantityAdjusted, setQuantityAdjusted] = useQueryState("quantityAdjusted", parseAsBoolean.withDefault(false));

  const { disableCheckout: externalDisableCheckout } = useCartPageContext();
  const {
    aggregates,
    disableCheckout,
    isPnaLoading,
    orderablesViewModel,
    bundleViewModels,
    invalidItems,
    handleInsertAlternateRow,
    handleCloseAlternateRow,
    handleDeleteSection,
    handleUpdatePriceReferenceForCart,
    handleSwitchProductVersionForCart,
  } = useB2BCartPageViewModel(externalDisableCheckout);

  const {
    totalWeight,
    totalSum,
    totalDiscount,
    hasAnySurcharge,
    hasAnyTariffSurcharge,
    surchargeValue,
    containsHousingKits,
    totalItemCount,
    productAmount,
    accountNumber,
    requestInFlight,
  } = aggregates;

  const showPnALoading = isPnaLoading && productAmount <= CART_PNA_THRESHOLD;
  const invalidLineItemAmount = invalidItems.length;
  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const scrollToRefs = useRef<Dict<ProductEntryListRef>>({});

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  const handleOpenPriceRefSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.choosePriceReferenceForCart.snackbar", { count }),
    });
  };

  const handleOpenSwitchProductSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.switchProductVersion.snackbar", { count }),
    });
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const snackbarValue = urlParams.get("switched-products-count");

    if (snackbarValue) {
      handleOpenSwitchProductSnackBar(parseInt(snackbarValue));
      removeUrlParam("switched-products-count");
    }
  }, []);

  useEffect(() => {
    if (recentlyAddedItem) {
      setSnackbarData({
        open: true,
        message: t("cartPage.addProduct.success.snackbar"),
      });

      const scrollRef = scrollToRefs.current[orderablesViewModel.tableId];
      const firstRow = orderablesViewModel.rows.find((r) => r.kind === "entry");
      if (firstRow && firstRow.kind === "entry") {
        scrollRef?.scrollToProductGroupNumber(firstRow.productGroupNumber);
      }

      const timer = setTimeout(() => {
        setRecentlyAddedItem(null);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [recentlyAddedItem, orderablesViewModel.rows.length]);

  const removeUrlParam = (key: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    window.history.replaceState({}, "", url);
  };

  const checkOutBtnSection = (
    <>
      {!isPunchoutEnabled() && <CheckoutButton disabled={disableCheckout} />}
      {isPunchoutEnabled() && (
        <>
          <PunchoutCancelRequisition />
          <PunchoutPlaceRequisition disabled={disableCheckout} />
        </>
      )}
    </>
  );

  const ButtonSection = (
    <Stack gap={1} direction={"row"}>
      <CartQuickActionsButton
        disableReportDownload={isPnaLoading}
        switchProductVersionForCart={handleSwitchProductVersionForCart}
        updatePriceReferenceForCart={(ref) => handleUpdatePriceReferenceForCart(ref, handleOpenPriceRefSnackBar)}
      />
      {checkOutBtnSection}
    </Stack>
  );

  const isCPDProgressEnabled = window.SCHA.feature.cpdBannerCart;

  return (
    <>
      <DstErrorMessage />
      <Snackbar
        open={quantityAdjusted}
        message={t("product.phaseout.qtyAdjusted.past", "Your quantity has been adjusted")}
        onClose={() => setQuantityAdjusted(null)}
        action={<SnackbarCloseButton onClick={() => setQuantityAdjusted(null)} />}
      />
      <Snackbar
        open={snackbarData.open}
        message={snackbarData.message}
        onClose={() => {
          setSnackbarData((prev) => ({ ...prev, open: false }));
          setRecentlyAddedItem(null);
        }}
        action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
      />
      <Stack gap={3}>
        <PageTitle tag="div" className="mb-0">
          <div style={{ marginBottom: "16px" }} className="d-flex flex-justify-between flex-align-center">
            <div>{t("cartPage.title", { accNo: accountNumber, count: totalItemCount })}</div>
            <div data-test-id={"cart-action-bar"}>{ButtonSection}</div>
          </div>
        </PageTitle>

        {showPnALoading && <Spinner />}

        {invalidLineItemAmount > 0 && (
          <CheckMessagesComponent
            data-test-id={"cart-issue-with-product"}
            msgType={"error"}
            msgCount={invalidLineItemAmount}
            onScrollToMessage={(index) => {
              const data = invalidItems[index];
              scrollToRefs.current[data.tableId]?.scrollToProductGroupNumber(data.productGroupNumber);
            }}
            sticky={true}
          />
        )}

        {orderablesViewModel.rows.length > 0 && (
          <ProductTableV2
            viewModel={orderablesViewModel}
            onClear={handleDeleteSection}
            data-test-id={"cart-section-regular"}
            scrollRef={(ref) => (scrollToRefs.current[orderablesViewModel.tableId] = ref!)}
            onInsertAlternateRow={handleInsertAlternateRow}
            onCloseAlternateRow={handleCloseAlternateRow}
          />
        )}

        {bundleViewModels.map((bundleViewModel) => (
          <IsBundledQuoteContextProvider value={true}>
            <CartSectionTitle
              title={t("page.cart.bundledQuote.title", {
                amount: bundleViewModel.rows.filter((r) => r.kind === "entry").length,
                quoteNumber: bundleViewModel.quoteNumber,
              })}
              subtitle={t("page.cart.bundledQuote.subtitle")}
            />
            <ProductTableV2
              viewModel={bundleViewModel}
              onClear={() => handleDeleteSection(bundleViewModel.quoteNumber)}
              data-test-id={`cart-section-bundle-${bundleViewModel.quoteNumber}`}
              scrollRef={(ref) => (scrollToRefs.current[bundleViewModel.tableId] = ref!)}
            />
          </IsBundledQuoteContextProvider>
        ))}
      </Stack>

      <Box sx={{ display: "flex", flexFlow: "column", alignItems: "flex-end", my: 4 }}>
        <OrderSummary sx={{ justifySelf: "flex-end" }}>
          <OrderSummary.DataContainer>
            <OrderSummary.WeightRow weight={totalWeight} />
            <OrderSummary.SurchargeRow
              surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
            />
            <OrderSummary.DiscountRow discount={totalDiscount} />
            <OrderSummary.TotalRow
              value={webservicesDisabled ? null : totalSum}
              showPriceOnRequest
              asterisk={hasAnySurcharge || hasAnyTariffSurcharge}
            />
            <OrderSummary.TaxDisclaimer />
          </OrderSummary.DataContainer>
          <OrderSummary.Divider />
          <OrderSummary.Actions>{checkOutBtnSection}</OrderSummary.Actions>
          {isCPDProgressEnabled && (
            <ConnectedCPDProgress totalCartValue={totalSum?.value} sx={{ mt: "24px", width: "100%" }} />
          )}
        </OrderSummary>
      </Box>

      <Stack gap={2}>
        <Alert severity="info">
          <div data-test-id={"info-disclaimer"}>{t("cartPage.disclaimer")}</div>
        </Alert>
        <DisclaimerCountryOrigin />
        {containsHousingKits && (
          <Stack gap={2}>
            <Alert severity="info">
              <div data-test-id={"info-housing-disclaimer-1"}>{t("cartPage.housingDisclaimer.1")}</div>
            </Alert>
            <Alert severity="info">
              <div data-test-id={"info-housing-disclaimer-2"}>{t("cartPage.housingDisclaimer.2")}</div>
            </Alert>
          </Stack>
        )}
        {<IncludesSurchargeDisclaimer hasSurcharge={hasAnySurcharge} tariffSurcharge={hasAnyTariffSurcharge} />}
      </Stack>

      {requestInFlight && <FullscreenLoader />}
    </>
  );
});

const DstErrorMessage = () => {
  const { t } = useTranslation();
  const { val, setTrue, setFalse } = useBooleanState(false);
  const dstError = useSelector(selectIsDstError);
  useEffect(() => {
    if (dstError) {
      setTrue();
      sendInfoWanrSeenCart();
    }
  }, [dstError]);

  return (
    <>
      {val && (
        <ToastMessage type={"warning"} onClose={() => setFalse()}>
          {t("cartPage.warnMsg.dst")}
        </ToastMessage>
      )}
    </>
  );
};
